/**
 * Loads the data behind a comparison: each run's metadata (Tier 1) and its full result
 * list (one bounded request per run), then aligns them into the comparison matrix.
 *
 * Running runs keep polling; settled runs do not. The heavy per-case detail (full output
 * JSON for the diff) is NOT loaded here — the drawer fetches it lazily per opened case.
 */
import { useMemo } from "react";
import { useQueries, type Query } from "@tanstack/react-query";
import {
  fetchBenchmarkRun,
  fetchCompactRunResults,
  type BenchmarkResult,
  type BenchmarkRun,
} from "@/lib/api/resources/benchmark";
import {
  alignResults,
  buildRuns,
  checkCompatibility,
  type CompareMatrix,
  type CompareRun,
  type Compatibility,
} from "../lib/align";

export interface CompareData {
  runs: CompareRun[];
  matrix: CompareMatrix;
  compatibility: Compatibility;
  loading: boolean;
  /** Ids in the URL that failed to load (e.g. a deleted run) — surfaced, not fatal. */
  missingIds: string[];
}

const isActive = (run?: BenchmarkRun): boolean =>
  run?.status === "running" || run?.status === "pending";

export const useCompareData = (runIds: string[]): CompareData => {
  // Dedupe so a hand-edited / shared URL like ?runs=a,a cannot create duplicate
  // columns and colliding React keys downstream.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ids = useMemo(() => Array.from(new Set(runIds)), [runIds.join(",")]);

  const runQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["benchmark_run", id],
      queryFn: () => fetchBenchmarkRun(id),
      enabled: !!id,
      refetchInterval: (query: Query) =>
        isActive(query.state?.data as BenchmarkRun | undefined) ? 5000 : false,
    })),
  });

  const resultQueries = useQueries({
    queries: ids.map((id, i) => ({
      queryKey: ["benchmark_compare_results", id],
      queryFn: () => fetchCompactRunResults(id),
      enabled: !!id,
      // Poll results only while the run is still producing them.
      refetchInterval: () => (isActive(runQueries[i]?.data) ? 5000 : false),
    })),
  });

  const loading =
    runQueries.some((q) => q.isLoading) ||
    resultQueries.some((q) => q.isLoading);
  // Kept outside the alignment memo so it reacts to an error even when retained data
  // leaves dataUpdatedAt unchanged (e.g. a polling run gets deleted mid-session).
  const missingIds = ids.filter((_, i) => runQueries[i]?.isError);

  // Recompute the (heavier) alignment only when a run or its results actually change.
  const runStamp = runQueries.map((q) => q.dataUpdatedAt).join(",");
  const resultStamp = resultQueries.map((q) => q.dataUpdatedAt).join(",");

  const aligned = useMemo(() => {
    const entries = ids
      .map((id, i) => ({ id, run: runQueries[i]?.data }))
      .filter((e): e is { id: string; run: BenchmarkRun } => !!e.run);
    const runs = buildRuns(entries);

    const resultsByRun: Record<string, BenchmarkResult[]> = {};
    runs.forEach((cr) => {
      const i = ids.indexOf(cr.id);
      resultsByRun[cr.id] = resultQueries[i]?.data ?? [];
    });

    const matrix = alignResults(runs, resultsByRun);
    const compatibility = checkCompatibility(runs, matrix);
    return { runs, matrix, compatibility };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(","), runStamp, resultStamp]);

  return { ...aligned, loading, missingIds };
};
